/* eslint-disable no-param-reassign */
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { type DefaultRootState } from "react-redux";

import { type MessageEntry, type TranscriptState } from "./types";

const countWords = (text: string): number =>
  text
    .replace(/•+/g, "REDACTEDWORD")
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]|_/g, "")
    .replace(/\s+/g, " ")
    .split(/\s+/)
    .filter(Boolean).length || 1;

const initialState: TranscriptState = {
  conversationId: undefined,
  indexCounter: 0,
  transcriptSegments: [],
  messages: [],
};

const transcript = createSlice({
  name: "transcript",
  initialState,
  reducers: {
    setupConversation(
      state,
      action: PayloadAction<{
        conversationId: string;
        transcriptSegments: string[];
      }>,
    ) {
      const { conversationId, transcriptSegments } = action.payload;

      return { ...initialState, conversationId, transcriptSegments };
    },

    setIndexCounter(state) {
      state.indexCounter = 0;
    },

    incrementIndex(state) {
      state.indexCounter += 1;
    },

    addMessage(
      state,
      action: PayloadAction<{ index: number; content: string }>,
    ) {
      const { index, content } = action.payload;
      state.messages.push({ index, content, segments: [] });
    },

    assignSegments(state) {
      const { transcriptSegments, messages, conversationId } = state;

      if (!conversationId || !transcriptSegments.length || !messages.length) {
        messages.forEach((message) => {
          message.segments = [];
        });

        return;
      }

      const sortedMessages = [...messages].sort((a, b) => a.index - b.index);
      const messageWordCounts = sortedMessages.map((message) => ({
        message,
        wordCount: countWords(message.content),
      }));
      const totalWords =
        messageWordCounts.reduce((sum, { wordCount }) => sum + wordCount, 0) ||
        1;

      let segmentPointer = 0;

      messages.forEach((message) => {
        const entry = messageWordCounts.find(
          (item) => item.message.index === message.index,
        );

        if (!entry) {
          message.segments = [];

          return;
        }

        const segmentCount = Math.round(
          (entry.wordCount / totalWords) * transcriptSegments.length,
        );
        message.segments = transcriptSegments.slice(
          segmentPointer,
          segmentPointer + segmentCount,
        );
        segmentPointer += segmentCount;
      });
    },

    setTranscriptSegments(state, action: PayloadAction<string[]>) {
      state.transcriptSegments = action.payload;
    },
  },
});

export const {
  incrementIndex,
  addMessage,
  setTranscriptSegments,
  assignSegments,
  setIndexCounter,
  setupConversation,
} = transcript.actions;
export const { reducer: transcriptReducer } = transcript;

export const select = {
  conversationId: (state: DefaultRootState): string | undefined =>
    state.transcript.conversationId,

  indexCounter: (state: DefaultRootState): number =>
    state.transcript.indexCounter,

  transcriptSegments: (state: DefaultRootState): string[] =>
    state.transcript.transcriptSegments,

  allMessages: (state: DefaultRootState): MessageEntry[] =>
    state.transcript.messages,

  messageByIndex: (
    state: DefaultRootState,
    idx: number,
  ): MessageEntry | undefined =>
    state.transcript.messages.find((m) => m.index === idx),

  messageContent: (state: DefaultRootState, idx: number): string =>
    select.messageByIndex(state, idx)?.content ?? "",

  segmentsForIndex: (state: DefaultRootState, idx: number): string[] =>
    select.messageByIndex(state, idx)?.segments ?? [],
};

export const selectConversationId = select.conversationId;
export const selectIndexCounter = select.indexCounter;
export const selectTranscriptSegments = select.transcriptSegments;
export const selectAllMessages = select.allMessages;
export const selectMessageByIndex = select.messageByIndex;
export const selectMessageContent = select.messageContent;
export const selectSegmentsForIndex = select.segmentsForIndex;
